import {
  Drawer,
  Box,
  SwipeableDrawer,
  Typography,
  Collapse,
  IconButton,
} from '@mui/material'
import { inject, observer } from 'mobx-react'
import React, { Fragment, memo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import HeadPhone from '../components/image/HeadPhone'
import MainLayout from '../layouts/MainLayout'
import { ACL_ACTION_TYPE, MODULE_CODE } from '../utils/constants'
import Avatar from '@mui/material/Avatar'
import { getNameInCapitalize } from '../utils/common'
import { EmptyText } from '../components/common/CellText'
import { useTheme } from '@mui/material'

const Drawers = ({
  openDrawer,
  handleOpenDrawer,
  handleCloseDrawer,
  authenticationStore,
  aclStore,
  moduleStore,
}) => {
  const pJson = require('../../package.json')
  const navigate = useNavigate()
  const { currentUser, isAccountAdmin, isSuperAdmin, currentUserAvatar } =
    authenticationStore
  const theme = useTheme()
  const nullText = <EmptyText>Chưa cập nhật</EmptyText>
  const [openUtilityMenu, setOpenUtilityMenu] = useState(false)

  // Access control check
  const isAccessControl = (moduleCode, accessControlType) => {
    if (isSuperAdmin) return true
    if (!moduleStore.checkAccessModule(moduleCode)) return false
    return accessControlType
      ? isAccountAdmin ||
          !!aclStore?.aclActionsByUser.find(
            item => item.code === accessControlType
          )?.status
      : true
  }

  // Reusable MenuItem component
  const MenuItem = ({ label, path }) => (
    <Box
      onClick={() => navigate(path)}
      sx={{
        width: '100%',
        padding: '10px 15px',
        borderRadius: '8px',
        cursor: 'pointer',
        backgroundColor: theme.palette.grey[100],
        mb: 1,
        textAlign: 'left',
        '&:hover': {
          backgroundColor: theme.palette.grey[200],
        },
      }}>
      <Typography
        variant="body2"
        color={theme.palette.primary.main}
        fontWeight="bold">
        {label}
      </Typography>
    </Box>
  )

  // Define the sub-menu items for "Tiện ích"
  const utilityMenuItems = [
    // { label: 'Tài liệu ISO', path: '/utility/iso-documents' },
    // { label: 'VB Pháp chế', path: '/utility/legal-docs' },
    // { label: 'Ký số điện tử', path: '/utility/digital-signature' },
    // {
    //   label: 'Tin nhắn liên thông',
    //   path: '/utility/interdepartmental-messages',
    // },
    { label: 'Tin nhắn nội bộ', path: '/utility/internal-message/receive' },
    // { label: 'KPI', path: '/utility/kpi' },
  ]

  // Drawer content
  const list = (
    <Box
      sx={{
        width: '350px',
        padding: '20px',
        color: theme.palette.primary.main,
        backgroundColor: theme.palette.common.white,
        borderRadius: '0 10px 10px 0',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        height: '100vh',
        position: 'relative', // Allow positioning of the support box
      }}
      role="presentation">
      {/* Logo Section */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        width="100%"
        mb={2}>
        <img
          src="/Frame.png"
          alt="Logo"
          style={{ width: '90px', height: '35px' }}
        />
      </Box>

      {/* Profile Section */}
      <Box my={2} display="flex" flexDirection="column" alignItems="center">
        <Avatar
          alt=""
          src={currentUserAvatar && URL.createObjectURL(currentUserAvatar)}
          sx={{
            width: '60px',
            height: '60px',
            border: '1px solid',
            borderColor: theme.palette.primary.main,
            mb: 1,
          }}
        />
        <Typography variant="h6" fontWeight="bold" color="text.primary">
          {getNameInCapitalize(currentUser?.name_lowercase) || nullText}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          onClick={() => navigate('/my-profile')}
          sx={{
            cursor: 'pointer',
            textDecoration: 'none',
            color: theme.color.gray,
            transition: 'color 0.3s ease',
            '&:hover': {
              color: 'primary.main',
              textDecoration: 'none',
            },
            '&:active': {
              color: 'secondary.main',
            },
          }}>
          Xem trang cá nhân
        </Typography>
      </Box>

      {/* Menu sidebar Section */}
      <Box mt={3} width="100%">
        <MenuItem
          label="Thông báo chung"
          path="/utility/general-notifications"
        />
        <MenuItem label="Danh bạ" path="/utility/contacts/users" />
        <MenuItem label="Nhiệm vụ" path="/mission" />
        <MenuItem label="Bảng lương" path="/proposal-salary-request" />
      </Box>

      {/* Tiện ích Collapsible Menu Section */}
      <Box mt={0} width="100%">
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            cursor: 'pointer',
            padding: '10px 15px',
            borderRadius: '8px',
            backgroundColor: theme.palette.grey[100],
            '&:hover': {
              backgroundColor: theme.palette.grey[200],
            },
          }}
          onClick={() => setOpenUtilityMenu(!openUtilityMenu)}>
          <Typography
            variant="body2" // Ensure this matches the other menu items
            fontWeight="bold"
            color={theme.palette.primary.main}>
            Tiện ích
          </Typography>
          {openUtilityMenu ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Box>

        {/* Collapsible Sub-menu Items */}
        <Collapse in={openUtilityMenu} timeout="auto" unmountOnExit>
          <Box pl={2} pt={1}>
            {utilityMenuItems.map((item, index) => (
              <MenuItem key={index} label={item.label} path={item.path} />
            ))}
          </Box>
        </Collapse>
      </Box>

      {/* Support and Version Info Section at the Bottom */}
      <Box
        mt={3}
        mb={1}
        display="flex"
        flexDirection="column"
        alignItems="center"
        sx={{
          position: 'absolute',
          bottom: '20px',
          width: '100%',
        }}>
        <Box display="flex" alignItems="center" gap={1}>
          <HeadPhone />
          <Typography variant="body2" fontWeight="bold" color="text.primary">
            Hỗ trợ
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary">
          v - {pJson.version}
        </Typography>
      </Box>
    </Box>
  )

  return (
    <Fragment>
      <SwipeableDrawer
        sx={{
          '& .MuiDrawer-paper': {
            background: theme.palette.common.white,
            boxShadow: `0 4px 12px rgba(0, 0, 0, 0.1)`,
            borderRadius: '0 10px 10px 0',
          },
        }}
        open={openDrawer}
        onOpen={handleOpenDrawer}
        onClose={handleCloseDrawer}
        disableSwipeToOpen={false}>
        {list}
      </SwipeableDrawer>
    </Fragment>
  )
}

export default memo(
  inject('authenticationStore', 'moduleStore', 'aclStore')(observer(Drawers))
)
